/* LEGAL-NOTICE
   © Copyright 2018 - Quid Informatica S.p.A. - All Rights Reserved
   https://quidinfo.it/qinetic/licence.html - "Quid Consumer Lending Platform"
   Deposito SIAE n. 012702 del 26 Luglio 2018
LEGAL-NOTICE */
import { AuthRequestService } from './auth-request.service';
import { ApiService } from '@api/services/api/api.service';
import { mergeMap, map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Observable, of, forkJoin } from 'rxjs';
import { UserDataDTO, AuthenticateDTO } from '@api/models';
import { HttpResponse } from '@angular/common/http';
import { AppService } from '@services/app/app.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  /**
   * sessione utente
   */
  private authUser: UserDataDTO;

  /**
   * lista di azioni che vengono eseguite dopo la logon
   */
  private afterLogonActions: Observable<void>[] = [];

  constructor(
    private appService: AppService,
    private apiService: ApiService,
    private authRequestService: AuthRequestService
  ) {}

  /**
   * effettua login
   */
  login(): Observable<boolean> {
    const data: AuthenticateDTO = {
      'application-code': this.getAuthAppCode(),
      sender: this.getAuthSender()
    };

    const testAttr = this.getTestAttr();

    if (!!testAttr) {
      data['test-attribute'] = testAttr;
    }

    return this.authenticate(data).pipe(mergeMap(() => this.loadAuthUser()));
  }

  /**
   * effettua login tramite auth code
   */
  loginWithAuthCode(code: string): Observable<boolean> {
    const data: AuthenticateDTO = {
      'uuid-info': code,
      'application-code': this.getAuthAppCode()
    };

    return this.authenticate(data).pipe(mergeMap(() => this.loadAuthUser()));
  }

  /**
   * effettua il logout e restituisce l'url per fare redirect
   */
  logout(): Observable<string> {
    return this.apiService
      .postSecurityLogout({
        observe: 'response'
      })
      .pipe(
        map((res: HttpResponse<void>) => res.headers.get('redirectEndpoint'))
      );
  }

  /**
   * avvia sessione recuperando i dati dell'utente
   */
  loadAuthUser(): Observable<boolean> {
    return this.getUserData().pipe(
      map(res => this.setAuthUser(res)),
      mergeMap(() => this.runAfterLogonActions()),
      map(() => true)
    );
  }

  /**
   * aggiunge azione da eseguire dopo il logon
   */
  addActionAfterLogon(action: Observable<void>): void {
    if (action instanceof Observable) {
      this.afterLogonActions.push(action);
    }
  }

  /**
   * restituisce la sessione attuale
   */
  getAuthUser(): UserDataDTO {
    return (this.authUser && { ...this.authUser }) || {};
  }

  /**
   * determina se la sessione è impostata correttamente
   */
  isAuthenticated(): boolean {
    return !!this.isValidUser(this.authUser);
  }

  /**
   * verifica la validità dell'auth code
   */
  private authenticate(data: AuthenticateDTO): Observable<boolean> {
    return this.apiService
      .postSecurityLogon(data, {
        observe: 'response',
        responseType: 'text'
      })
      .pipe(
        map((res: HttpResponse<void>) => {
          this.authRequestService.saveAuthHeaderFromHeadersWhenNecessary(
            res && res.headers
          );
        }),
        map(() => true)
      );
  }

  /**
   * restituisce i dati dell'user autenticato
   */
  private getUserData(): Observable<UserDataDTO> {
    return this.apiService
      .getSecurityUserData()
      .pipe(map(res => this.sanitizeUserData(res)));
  }

  /**
   * imposta i dati della sessione e controlla che sia valida
   */
  private setAuthUser(authUser: UserDataDTO): void {
    if (!this.isValidUser(authUser)) {
      throw new Error(`User's session is not valid`);
    }

    this.authUser = (!!authUser && { ...authUser }) || null;
  }

  /**
   * controlla se la sessione è valida
   */
  private isValidUser(user: UserDataDTO): boolean {
    return !!user;
  }

  /**
   * esegue le operazioni registrate da effettuare dopo il logon
   */
  private runAfterLogonActions(): Observable<boolean> {
    if (this.afterLogonActions.length === 0) {
      return of(true);
    }

    return forkJoin(this.afterLogonActions).pipe(map(() => true));
  }

  /**
   * restituisce il sender per eseguire l'autenticazione
   */
  private getAuthSender(): string {
    return this.appService.getAppName('container');
  }

  /**
   * restituisce il codice application per eseguire l'autenticazione
   */
  private getAuthAppCode(): string {
    return this.appService.getEnvironment().appCode.toUpperCase();
  }

  /**
   * restituisce il valore dell'item test attribute
   */
  private getTestAttr(): string {
    return localStorage.getItem('test-attribute');
  }

  /**
   * controlla e sanifica i dati dalla userdata
   */
  private sanitizeUserData(data: UserDataDTO): UserDataDTO {
    if (
      data &&
      data.attributes &&
      typeof data.attributes['position-id'] === 'string'
    ) {
      data.attributes.positionId = data.attributes['position-id'].trim();
    }

    return data;
  }
}
